// src/api/payment-requests-api.js
import { gql } from '@apollo/client';
import client from './apollo-client';

// GraphQL Queries
export const GET_PAYMENT_REQUESTS = gql`
  query GetPaymentRequests($skip: Float, $take: Float, $status: String) {
    paymentRequests(skip: $skip, take: $take, status: $status) {
      id
      amount
      isApproved
      rejectionReason
      status
      dateDemande
      userId
      organismeId
      user {
        id
        email
        firstName
        lastName
        tel
        CIN
        RIB
        address
        userType
        isActive
        isPhoneVerified
        commissionBalance
        approvedCommission
        waitingCommission
        pharmacy {
          id
          namePharmacy
          region { id nomRegion }
          ville { id nomVilleVillage }
        }
        ownedLaboratory { id nom }
      }
      organisme {
        id
        nomOrganisme
        adresse
        nomContact
        prenomContact
        telContact
        defaultOrganisme
      }
      hasDuplicates
      transactionsCount
      processedAt
      createdAt
      updatedAt
    }
  }
`;

export const GET_PAYMENT_REQUESTS_COUNT = gql`
  query GetPaymentRequestsCount($status: String) {
    paymentRequestsCount(status: $status)
  }
`;

export const GET_PAYMENT_REQUEST = gql`
  query GetPaymentRequest($id: ID!) {
    paymentRequest(id: $id) {
      id
      amount
      isApproved
      rejectionReason
      status
      dateDemande
      userId
      organismeId
      user {
        id
        email
        firstName
        lastName
        tel
        CIN
        RIB
        address
        userType
        isActive
        isPhoneVerified
        commissionBalance
        approvedCommission
        waitingCommission
        pharmacy {
          id
          namePharmacy
          telPharmacy
          commissionBalance
          regionId
          villeId
          region {
            id
            nomRegion
          }
          ville {
            id
            nomVilleVillage
          }
        }
        ownedLaboratory {
          id
          nom
        }
        transactions {
          id
          date
          gain
          status
          type
          product {
            id
            name
            price
          }
        }
      }
      organisme {
        id
        nomOrganisme
        adresse
        nomContact
        prenomContact
        telContact
        defaultOrganisme
      }
      transactions {
        id
        date
        gain
        companyCommission
        status
        type
        avantGain
        apresGain
        product {
          id
          name
          price
          description
          image
        }
        label {
          id
          code
          userId
          promotion {
            id
            name
          }
        }
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_PAYMENT_REQUESTS_BY_USER = gql`
  query GetPaymentRequestsByUser($userId: ID!) {
    paymentRequestsByUser(userId: $userId) {
      id
      amount
      isApproved
      rejectionReason
      status
      dateDemande
      organismeId
      organisme {
        id
        nomOrganisme
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_PAYMENT_REQUESTS_BY_STATUS = gql`
  query GetPaymentRequestsByStatus($status: PaymentStatus!) {
    paymentRequestsByStatus(status: $status) {
      id
      amount
      isApproved
      rejectionReason
      status
      dateDemande
      userId
      user {
        id
        firstName
        lastName
        email
        userType
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_PAYMENT_REQUESTS_STATISTICS = gql`
  query GetPaymentRequestsStatistics {
    paymentRequestsStatistics {
      total
      pending
      approved
      rejected
      totalAmount
      pendingAmount
      approvedAmount
      rejectedAmount
      averageAmount
      averageProcessingTime
      topRequesters {
        user {
          id
          firstName
          lastName
          email
          userType
        }
        totalRequests
        totalAmount
      }
      monthlyStats {
        month
        total
        approved
        rejected
        amount
      }
    }
  }
`;

// GraphQL Mutations
export const CREATE_PAYMENT_REQUEST = gql`
  mutation CreatePaymentRequest($userId: ID!) {
    CreatePaymentRequest(userId: $userId) {
      ok
      paymentRequest {
        id
        amount
        isApproved
        status
        dateDemande
        userId
        user {
          id
          firstName
          lastName
          email
          commissionBalance
        }
      }
    }
  }
`;

export const APPROVE_PAYMENT_REQUEST = gql`
  mutation ApprovePaymentRequest($id: ID!, $approvalMessage: String) {
    approvePaymentRequest(id: $id, approvalMessage: $approvalMessage) {
      id
      amount
      isApproved
      status
      approvalMessage
      dateDemande
      userId
      user {
        id
        firstName
        lastName
        email
        commissionBalance
        approvedCommission
        waitingCommission
      }
      updatedAt
    }
  }
`;

export const REJECT_PAYMENT_REQUEST = gql`
  mutation RejectPaymentRequest($id: ID!, $rejectionReason: String!) {
    rejectPaymentRequest(id: $id, rejectionReason: $rejectionReason) {
      id
      amount
      isApproved
      rejectionReason
      status
      dateDemande
      userId
      user {
        id
        firstName
        lastName
        email
        commissionBalance
        approvedCommission
        waitingCommission
      }
      updatedAt
    }
  }
`;

export const UPDATE_PAYMENT_REQUEST = gql`
  mutation UpdatePaymentRequest(
    $id: ID!,
    $status: PaymentStatus,
    $isApproved: Boolean,
    $rejectionReason: String,
    $organismeId: String
  ) {
    updatePaymentRequest(
      id: $id,
      status: $status,
      isApproved: $isApproved,
      rejectionReason: $rejectionReason,
      organismeId: $organismeId
    ) {
      id
      amount
      isApproved
      rejectionReason
      status
      dateDemande
      organismeId
      updatedAt
    }
  }
`;

export const DELETE_PAYMENT_REQUEST = gql`
  mutation DeletePaymentRequest($id: ID!) {
    deletePaymentRequest(id: $id)
  }
`;

// Payment Requests API functions
export const paymentRequestsApi = {
  // Get all payment requests
  getPaymentRequests: async () => {
    try {
      const { data } = await client.query({
        query: GET_PAYMENT_REQUESTS,
        fetchPolicy: 'network-only'
      });
      return data.paymentRequests;
    } catch (error) {
      console.error('Get payment requests error:', error);
      throw error;
    }
  },

  // Get payment request by ID
  getPaymentRequest: async (id) => {
    try {
      const { data } = await client.query({
        query: GET_PAYMENT_REQUEST,
        variables: { id },
        fetchPolicy: 'network-only'
      });
      return data.paymentRequest;
    } catch (error) {
      console.error(`Get payment request ${id} error:`, error);
      throw error;
    }
  },

  // Get payment requests by user
  getPaymentRequestsByUser: async (userId) => {
    try {
      const { data } = await client.query({
        query: GET_PAYMENT_REQUESTS_BY_USER,
        variables: { userId },
        fetchPolicy: 'network-only'
      });
      return data.paymentRequestsByUser;
    } catch (error) {
      console.error(`Get payment requests by user ${userId} error:`, error);
      throw error;
    }
  },

  // Get payment requests by status
  getPaymentRequestsByStatus: async (status) => {
    try {
      const { data } = await client.query({
        query: GET_PAYMENT_REQUESTS_BY_STATUS,
        variables: { status },
        fetchPolicy: 'network-only'
      });
      return data.paymentRequestsByStatus;
    } catch (error) {
      console.error(`Get payment requests by status ${status} error:`, error);
      throw error;
    }
  },

  // Get payment requests statistics
  getPaymentRequestsStatistics: async () => {
    try {
      const { data } = await client.query({
        query: GET_PAYMENT_REQUESTS_STATISTICS,
        fetchPolicy: 'network-only'
      });
      return data.paymentRequestsStatistics;
    } catch (error) {
      console.error('Get payment requests statistics error:', error);
      throw error;
    }
  },

  // Create payment request
  createPaymentRequest: async (userId) => {
    try {
      const { data } = await client.mutate({
        mutation: CREATE_PAYMENT_REQUEST,
        variables: { userId },
        refetchQueries: [{ query: GET_PAYMENT_REQUESTS }]
      });
      return data.CreatePaymentRequest;
    } catch (error) {
      console.error('Create payment request error:', error);
      throw error;
    }
  },

  // Approve payment request
  approvePaymentRequest: async (id) => {
    try {
      const { data } = await client.mutate({
        mutation: APPROVE_PAYMENT_REQUEST,
        variables: { id },
        refetchQueries: [{ query: GET_PAYMENT_REQUESTS }]
      });
      return data.approvePaymentRequest;
    } catch (error) {
      console.error(`Approve payment request ${id} error:`, error);
      throw error;
    }
  },

  // Reject payment request
  rejectPaymentRequest: async (id, rejectionReason) => {
    try {
      const { data } = await client.mutate({
        mutation: REJECT_PAYMENT_REQUEST,
        variables: { id, rejectionReason },
        refetchQueries: [{ query: GET_PAYMENT_REQUESTS }]
      });
      return data.rejectPaymentRequest;
    } catch (error) {
      console.error(`Reject payment request ${id} error:`, error);
      throw error;
    }
  },

  // Update payment request
  updatePaymentRequest: async (updateData) => {
    try {
      const { data } = await client.mutate({
        mutation: UPDATE_PAYMENT_REQUEST,
        variables: updateData,
        refetchQueries: [{ query: GET_PAYMENT_REQUESTS }]
      });
      return data.updatePaymentRequest;
    } catch (error) {
      console.error(`Update payment request ${updateData.id} error:`, error);
      throw error;
    }
  },

  // Delete payment request
  deletePaymentRequest: async (id) => {
    try {
      const { data } = await client.mutate({
        mutation: DELETE_PAYMENT_REQUEST,
        variables: { id },
        refetchQueries: [{ query: GET_PAYMENT_REQUESTS }]
      });
      return data.deletePaymentRequest;
    } catch (error) {
      console.error(`Delete payment request ${id} error:`, error);
      throw error;
    }
  },

  // Helper functions for data processing
  filterPaymentRequests: (requests, filters) => {
    if (!filters) return requests;

    return requests.filter(request => {
      // Status filter
      if (filters.status && filters.status !== 'all' && request.status !== filters.status) {
        return false;
      }

      // User type filter
      if (filters.userType && filters.userType !== 'all' && request.user?.userType !== filters.userType) {
        return false;
      }

      // Date range filter
      if (filters.dateFrom) {
        const requestDate = new Date(request.dateDemande);
        const fromDate = new Date(filters.dateFrom);
        if (requestDate < fromDate) return false;
      }

      if (filters.dateTo) {
        const requestDate = new Date(request.dateDemande);
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999);
        if (requestDate > toDate) return false;
      }

      // Amount range filter
      if (filters.minAmount && request.amount < parseFloat(filters.minAmount)) return false;
      if (filters.maxAmount && request.amount > parseFloat(filters.maxAmount)) return false;

      // Search term filter
      if (filters.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        const searchableText = [
          request.id,
          request.user?.firstName || '',
          request.user?.lastName || '',
          request.user?.email || '',
          request.user?.tel || '',
          request.user?.pharmacy?.namePharmacy || '',
          request.user?.ownedLaboratory?.nom || '',
          request.rejectionReason || ''
        ].join(' ').toLowerCase();
        
        if (!searchableText.includes(term)) {
          return false;
        }
      }

      return true;
    });
  },

  sortPaymentRequests: (requests, sortBy = 'dateDemande', sortOrder = 'desc') => {
    return [...requests].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      // Handle special sorting cases
      if (sortBy === 'user') {
        aVal = `${a.user?.firstName || ''} ${a.user?.lastName || ''}`.toLowerCase();
        bVal = `${b.user?.firstName || ''} ${b.user?.lastName || ''}`.toLowerCase();
      }

      if (sortBy === 'status') {
        const statusOrder = { 'PENDING': 3, 'VALIDATED': 2, 'CANCELLED': 1 };
        aVal = statusOrder[aVal] || 0;
        bVal = statusOrder[bVal] || 0;
      }

      // Handle dates
      if (sortBy === 'dateDemande' || aVal instanceof Date || typeof aVal === 'string') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      if (sortOrder === 'desc') {
        return bVal - aVal;
      } else {
        return aVal - bVal;
      }
    });
  },

  // Calculate statistics from payment requests array
  calculateStatistics: (requests) => {
    const stats = {
      total: requests.length,
      pending: requests.filter(r => r.status === 'PENDING').length,
      approved: requests.filter(r => r.status === 'VALIDATED').length,
      rejected: requests.filter(r => r.status === 'CANCELLED').length,
      totalAmount: requests.reduce((sum, r) => sum + r.amount, 0),
      pendingAmount: requests.filter(r => r.status === 'PENDING').reduce((sum, r) => sum + r.amount, 0),
      approvedAmount: requests.filter(r => r.status === 'VALIDATED').reduce((sum, r) => sum + r.amount, 0),
      rejectedAmount: requests.filter(r => r.status === 'CANCELLED').reduce((sum, r) => sum + r.amount, 0)
    };

    // Calculate averages
    stats.averageAmount = stats.total > 0 ? stats.totalAmount / stats.total : 0;
    stats.approvalRate = stats.total > 0 ? (stats.approved / stats.total) * 100 : 0;
    stats.rejectionRate = stats.total > 0 ? (stats.rejected / stats.total) * 100 : 0;

    // Monthly breakdown
    const now = new Date();
    stats.thisMonth = requests.filter(r => {
      const requestDate = new Date(r.dateDemande);
      return requestDate.getMonth() === now.getMonth() && 
             requestDate.getFullYear() === now.getFullYear();
    }).length;

    stats.thisWeek = requests.filter(r => {
      const requestDate = new Date(r.dateDemande);
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return requestDate >= oneWeekAgo;
    }).length;

    return stats;
  },

  // Get top requesters
  getTopRequesters: (requests, limit = 10) => {
    const userStats = {};
    
    requests.forEach(request => {
      if (request.user) {
        const userId = request.user.id;
        if (!userStats[userId]) {
          userStats[userId] = {
            user: request.user,
            totalRequests: 0,
            totalAmount: 0,
            approvedRequests: 0,
            approvedAmount: 0,
            pendingRequests: 0,
            pendingAmount: 0,
            rejectedRequests: 0,
            rejectedAmount: 0
          };
        }
        
        userStats[userId].totalRequests++;
        userStats[userId].totalAmount += request.amount;
        
        if (request.status === 'VALIDATED') {
          userStats[userId].approvedRequests++;
          userStats[userId].approvedAmount += request.amount;
        } else if (request.status === 'PENDING') {
          userStats[userId].pendingRequests++;
          userStats[userId].pendingAmount += request.amount;
        } else if (request.status === 'CANCELLED') {
          userStats[userId].rejectedRequests++;
          userStats[userId].rejectedAmount += request.amount;
        }
      }
    });
    
    return Object.values(userStats)
      .sort((a, b) => b.totalAmount - a.totalAmount)
      .slice(0, limit);
  },

  // Export to CSV format
  exportToCSV: (requests, filename = 'payment_requests') => {
    const headers = [
      'ID',
      'Date de demande',
      'Utilisateur',
      'Email',
      'Type d\'utilisateur',
      'Montant (DH)',
      'Statut',
      'Approuvé',
      'Raison de rejet',
      'Organisation',
      'Téléphone',
      'RIB',
      'Date de création',
      'Date de mise à jour'
    ];
    
    const csvData = requests.map(request => [
      request.id,
      new Date(request.dateDemande).toLocaleDateString('fr-FR'),
      `${request.user?.firstName || ''} ${request.user?.lastName || ''}`.trim(),
      request.user?.email || '',
      request.user?.userType || '',
      request.amount.toFixed(2),
      request.status,
      request.isApproved ? 'Oui' : 'Non',
      request.rejectionReason || '',
      request.user?.pharmacy?.namePharmacy || request.user?.ownedLaboratory?.nom || '',
      request.user?.tel || '',
      request.user?.RIB || '',
      new Date(request.createdAt).toLocaleDateString('fr-FR'),
      new Date(request.updatedAt).toLocaleDateString('fr-FR')
    ]);
    
    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');
    
    return csvContent;
  },

  // Bulk operations
  bulkApprove: async (requestIds) => {
    try {
      const promises = requestIds.map(id => 
        client.mutate({
          mutation: APPROVE_PAYMENT_REQUEST,
          variables: { id }
        })
      );
      
      const results = await Promise.all(promises);
      
      // Refetch all payment requests
      await client.refetchQueries({
        include: [GET_PAYMENT_REQUESTS]
      });
      
      return results.map(result => result.data.approvePaymentRequest);
    } catch (error) {
      console.error('Bulk approve error:', error);
      throw error;
    }
  },

  bulkReject: async (requestIds, rejectionReason) => {
    try {
      const promises = requestIds.map(id => 
        client.mutate({
          mutation: REJECT_PAYMENT_REQUEST,
          variables: { id, rejectionReason }
        })
      );
      
      const results = await Promise.all(promises);
      
      // Refetch all payment requests
      await client.refetchQueries({
        include: [GET_PAYMENT_REQUESTS]
      });
      
      return results.map(result => result.data.rejectPaymentRequest);
    } catch (error) {
      console.error('Bulk reject error:', error);
      throw error;
    }
  }
};